import { extractExactI18nLabel } from "~/common/functions/i18nLabel";
import type { ListEffectiveCategoryPropertySchemasQuery } from "~/features/admin/categories/gqls";
import type {
  CategoryDetailRaw,
  CategoryDetailUI,
  CategoryHierarchyDraft,
  CategoryHierarchyNode,
  CategoryRaw,
  CategoryRow,
  CategoryTableViewRaw,
  PropertySchemaRow,
  RelationTypeRow,
} from "~/features/admin/categories/types";
import type { Locale } from "~/i18n/resolveLocale";

/** Response shape of `cnutListEffectiveCategoryPropertySchemas` (the union member with data). */
type EffectiveCategoryPropertySchemasResult =
  ListEffectiveCategoryPropertySchemasQuery["cnutListEffectiveCategoryPropertySchemas"];

/** API row → table row. */
function toCategoryRow(raw: CategoryRaw, locale: Locale): CategoryRow {
  return {
    id: raw.id,
    typeId: raw.typeId,
    displayName: raw.name,
    // Label column: the locale label itself — strict, no fallback. Empty when
    // the row has no value for the requested locale.
    localeLabel: extractExactI18nLabel(raw.i18nLabel as Record<string, unknown> | null, locale) ?? "",
    description: raw.description ?? "",
    relationTypeLabel: raw.relationType?.name ?? "",
    documentUrl: raw.documentUrl ?? null,
    isDeactivated: Boolean(raw.deactivatedAt),
  };
}

export function toCategoryRowList(raws: readonly CategoryRaw[], locale: Locale): CategoryRow[] {
  return raws.map((raw) => toCategoryRow(raw, locale));
}

type EffectivePropertySchemaItem = Extract<
  EffectiveCategoryPropertySchemasResult,
  { schemas: unknown }
>["schemas"][number];

/** One effective-schema item → table row. `categoryNames` resolves the definer's display name. */
function toPropertySchemaRow(item: EffectivePropertySchemaItem, categoryNames: Map<string, string>): PropertySchemaRow {
  const sourceCategoryName = categoryNames.get(item.sourceCategoryId);
  if (!sourceCategoryName) {
    throw new Error(`Source category not found: ${item.sourceCategoryId}`);
  }

  return {
    // The effective query has no per-association id — synthesize a stable key from definer + metadataKey.
    id: `${item.sourceCategoryId}-${item.metadataKey}`,
    name: item.metadataKey,
    description: item.description ?? "",
    position: item.position,
    required: item.required,
    unique: item.unique,
    propertyTypeId: item.type,
    propertyAttributeTypeId: item.attributeType ?? null,
    selectOptions: (item.selectOptions ?? []).map((opt) => ({
      value: opt.value,
      label: (opt.label ?? undefined) as { ja?: string; en?: string } | undefined,
    })),
    urlAllowList: (item.urlAllowList ?? []).map((entry) => ({
      value: entry.value ?? "",
      regex: entry.regex ?? "",
    })),
    isOwn: item.isOwn,
    conflict: item.conflict,
    sourceCategoryId: item.sourceCategoryId,
    sourceCategoryName,
  };
}

/**
 * The parent's effective schemas as seen from a child being created/edited:
 * every row is inherited, so `isOwn` is false regardless of who defines it.
 */
export function toInheritedPropertySchemaRows(
  effectiveSchemas: EffectiveCategoryPropertySchemasResult,
  categories: readonly CategoryRaw[],
): PropertySchemaRow[] {
  if (effectiveSchemas.__typename !== "ListEffectiveCategoryPropertySchemasOutput") return [];
  const categoryNames = new Map(categories.map((category) => [category.id, category.name]));
  // The form must not crash on a definer missing from the list (e.g. a stale cache) — show its id instead.
  for (const item of effectiveSchemas.schemas) {
    if (!categoryNames.has(item.sourceCategoryId)) categoryNames.set(item.sourceCategoryId, item.sourceCategoryId);
  }
  return effectiveSchemas.schemas.map((item) => ({ ...toPropertySchemaRow(item, categoryNames), isOwn: false }));
}

/** API detail row → detail-page view model. */
export function toCategoryDetailUI(
  raw: CategoryDetailRaw,
  effectiveSchemas: EffectiveCategoryPropertySchemasResult,
  categories: readonly CategoryRaw[],
): CategoryDetailUI {
  const rt = raw.relationType;
  const categoryNames = new Map(categories.map((category) => [category.id, category.name]));

  const sourceRelations: RelationTypeRow[] = raw.sourceRelationTypes.map((srt) => ({
    relationType: { id: srt.id, name: srt.name },
    otherCategory: {
      id: srt.targetCategory.id,
      label: srt.targetCategory.name,
    },
  }));

  const targetRelations: RelationTypeRow[] = raw.targetRelationTypes.map((trt) => ({
    relationType: { id: trt.id, name: trt.name },
    otherCategory: {
      id: trt.sourceCategory.id,
      label: trt.sourceCategory.name,
    },
  }));

  // Union narrowing only — ValidationError cannot occur for a category the caller already sees.
  const effectiveItems =
    effectiveSchemas.__typename === "ListEffectiveCategoryPropertySchemasOutput" ? effectiveSchemas.schemas : [];
  const propertySchemas = effectiveItems.map((item) => toPropertySchemaRow(item, categoryNames));

  return {
    id: raw.id,
    name: raw.name,
    displayName: raw.name,
    description: raw.description ?? "",
    typeId: raw.typeId,
    categoryPath: raw.categoryPath?.path ?? "",
    documentUrl: raw.documentUrl ?? null,
    jaLabel: extractExactI18nLabel(raw.i18nLabel as Record<string, unknown> | null, "ja"),
    enLabel: extractExactI18nLabel(raw.i18nLabel as Record<string, unknown> | null, "en"),
    isDeactivated: Boolean(raw.deactivatedAt),
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
    deactivatedAt: raw.deactivatedAt ?? null,
    relationType:
      rt && rt.sourceCategory && rt.targetCategory
        ? {
            id: rt.id,
            name: rt.name,
            source: { id: rt.sourceCategory.id, label: rt.sourceCategory.name },
            target: { id: rt.targetCategory.id, label: rt.targetCategory.name },
          }
        : null,
    sourceRelations,
    targetRelations,
    propertySchemas,
  };
}

/* ============================================================
   Hierarchy — flat rows → nested tree by categoryPath.path.
   ============================================================ */

/**
 * Group flat categories into a hierarchical tree by `categoryPath.path`
 * (dot-delimited, e.g. "ctn.person"). Ported from V1's
 * `formatCategoriesForTableView` — pure, no React.
 */
export function formatCategoryHierarchy(rows: readonly CategoryTableViewRaw[]): CategoryHierarchyNode[] {
  const sorted = [...rows].sort((a, b) => {
    const al = a.categoryPath?.path?.split(".").length ?? 0;
    const bl = b.categoryPath?.path?.split(".").length ?? 0;
    return al - bl;
  });

  const pathsMap = new Map<string, CategoryHierarchyDraft>();

  for (const cat of sorted) {
    const path = cat.categoryPath?.path;
    if (!path) continue;
    const parts = path.split(".");

    if (parts.length === 1) {
      pathsMap.set(path, cat);
    } else {
      const parentPath = parts.slice(0, -1).join(".");
      const parent = pathsMap.get(parentPath);
      if (parent) {
        const next = { ...parent, children: [...(parent.children ?? []), path] };
        pathsMap.set(parentPath, next);
      }
      pathsMap.set(path, cat);
    }
  }

  const traverse = (node: CategoryHierarchyDraft): CategoryHierarchyNode => {
    const { children, ...rest } = node;
    const out: CategoryHierarchyNode = { ...rest };
    if (children?.length) {
      out.children = children
        .map((childPath) => {
          const child = pathsMap.get(childPath);
          if (!child) return null;
          pathsMap.delete(childPath);
          return traverse({ ...child });
        })
        .filter((c): c is CategoryHierarchyNode => c !== null);
    }
    return out;
  };

  const result: CategoryHierarchyNode[] = [];
  for (const [key, value] of pathsMap.entries()) {
    if (value.categoryPath?.path === key) {
      result.push(traverse(value));
    }
  }
  return result;
}

/* ============================================================
   Lineage — flat rows → graph nodes + edges for xyflow.
   ============================================================ */

export interface LineageNodeData {
  /** Real category id (empty string for synthetic "fake" parent nodes). */
  categoryId: string;
  /** Last segment of the path or full path for synthetic nodes. */
  name: string;
  /** Strict per-locale label — empty when no value for the current locale (or for fake nodes). */
  localeLabel: string;
  /** Full dot-delimited path — used as the node id. */
  path: string;
  description: string;
  /** True when this node was synthesized to fill a gap in the path tree. */
  isFake: boolean;
}

export interface LineageEdge {
  id: string;
  source: string;
  target: string;
}

/**
 * Build the node set for the lineage graph. Walks each category's path and
 * synthesizes any missing ancestors as "fake" nodes so the tree is fully
 * connected even when an intermediate path has no row of its own.
 */
export function buildLineageNodes(rows: readonly CategoryRaw[], locale: Locale): LineageNodeData[] {
  const sorted = [...rows].sort((a, b) => {
    const al = a.categoryPath?.path?.split(".").length ?? 0;
    const bl = b.categoryPath?.path?.split(".").length ?? 0;
    return bl - al;
  });

  const pathsMap = new Map<string, LineageNodeData>();

  for (const category of sorted) {
    const fullPath = category.categoryPath?.path;
    if (!fullPath) continue;
    const parts = fullPath.split(".");

    while (parts.length > 0) {
      const path = parts.join(".");
      if (path === fullPath) {
        pathsMap.set(path, {
          categoryId: category.id,
          name: category.name,
          // Strict per-locale read — no cross-locale fallback (matches V1 PR #6567).
          localeLabel: extractExactI18nLabel(category.i18nLabel as Record<string, unknown> | null, locale) ?? "",
          path,
          description: category.description ?? "",
          isFake: false,
        });
      } else if (!pathsMap.has(path)) {
        pathsMap.set(path, {
          categoryId: "",
          name: parts.length > 1 ? parts.slice(1).join(".") : path,
          localeLabel: "",
          path,
          description: "",
          isFake: true,
        });
      }
      parts.pop();
    }
  }

  return Array.from(pathsMap.values());
}

/** Edges connect each node to its parent path (one level up). */
export function buildLineageEdges(nodes: readonly LineageNodeData[]): LineageEdge[] {
  return nodes.flatMap((node) => {
    const parentPath = node.path.split(".").slice(0, -1).join(".");
    return parentPath ? [{ id: `${parentPath}->${node.path}`, source: parentPath, target: node.path }] : [];
  });
}
